import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { newPlacesAPI } from '@/services/newPlacesAPI';
import Icon from '@/components/ui/Icon';
import { fetchPlaceDetails } from '@/services/placesAPI';
import { transformGooglePlaceForDB } from '@/utils/googlePlacesTransform';
import { validateYouthProgram } from '@/services/placeValidationService';
import { saveCampToDatabase } from '@/services/campDatabaseService';

/**
 * Modern LocationAutocomplete using Google Places API (NEW)
 * Uses REST-based Places API, NOT the legacy JavaScript widget
 * 
 * Features:
 * - REST API calls (no deprecated JavaScript API)
 * - Searches both locations AND businesses
 * - Automatic categorization by place type
 * - Session tokens for cost optimization
 * - Location bias towards Atlanta metro area
 */
const LocationAutocomplete = ({ 
  value = '',
  onChange,
  onPlaceSelect, 
  placeholder = "Search camps, organizations, or locations",
  onLocationSelect, // Backward compatibility
  onBusinessSelect, // Backward compatibility
  onEnter, // Optional callback when Enter is pressed without selecting a suggestion
  className = "",
  disabled = false,
  addressMode = false // When true, uses address-focused autocomplete for addresses, cities, states, zip codes
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false); // Loading state for place details
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Sync external value prop with internal query state
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Debounced search function
  const searchPlaces = useCallback(async (input) => {
    if (!input.trim() || input.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔍 LocationAutocomplete: Starting search for:', input.substring(0, 50));
      console.log('🔍 LocationAutocomplete: addressMode:', addressMode);
      
      // Use address-focused autocomplete when addressMode is enabled
      const results = addressMode 
        ? await newPlacesAPI.autocompleteAddress(input)
        : await newPlacesAPI.autocomplete(input);
      
      console.log('✅ LocationAutocomplete: Received', results.length, 'results');
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('❌ LocationAutocomplete: Places search error:', {
        message: err.message,
        stack: err.stack,
        input: input.substring(0, 50),
        addressMode
      });
      
      // Provide user-friendly error messages based on error type
      let errorMessage = 'Unable to search locations. Please try again.';
      
      if (err.message && err.message.includes('API key')) {
        errorMessage = 'Google Places API is not configured. Please contact support.';
      } else if (err.message && err.message.includes('API key is invalid') || err.message.includes('API is not enabled')) {
        errorMessage = 'Google Places API configuration error. Please contact support.';
      } else if (err.message && err.message.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (err.message && (err.message.includes('network') || err.message.includes('fetch') || err.message.includes('Network error'))) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message && err.message.includes('Invalid request')) {
        errorMessage = 'Invalid search query. Please try a different location.';
      } else if (err.message) {
        // Use the error message from the API if available
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [addressMode]);

  // Handle input change with debouncing
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    
    // Call onChange prop to notify parent
    if (onChange) {
      onChange(newValue);
    }

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search
    debounceRef.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  }, [searchPlaces, onChange]);

  // Validate coordinates
  const validateCoordinates = useCallback((lat, lng) => {
    if (lat === undefined || lng === undefined || lat === null || lng === null) {
      return { valid: false, error: 'Coordinates are missing' };
    }
    
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    
    if (isNaN(latitude) || isNaN(longitude)) {
      return { valid: false, error: 'Invalid coordinate format' };
    }
    
    if (latitude < -90 || latitude > 90) {
      return { valid: false, error: 'Latitude must be between -90 and 90' };
    }
    
    if (longitude < -180 || longitude > 180) {
      return { valid: false, error: 'Longitude must be between -180 and 180' };
    }
    
    return { valid: true, latitude, longitude };
  }, []);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(async (suggestion) => {
    // Update UI state immediately for better UX
    // This ensures the input field always reflects the user's selection
    setQuery(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    setError(null);
    setLoadingDetails(true);

    try {
      // Fetch full place details using NEW API
      const placeDetails = await newPlacesAPI.getPlaceDetails(suggestion.place_id);
      
      // Validate coordinates before proceeding
      const coordValidation = validateCoordinates(
        placeDetails.geometry?.location?.lat,
        placeDetails.geometry?.location?.lng
      );
      
      if (!coordValidation.valid) {
        throw new Error(coordValidation.error || 'Invalid location coordinates');
      }
      
      // Transform to enriched format
      const enrichedPlace = {
        ...placeDetails,
        location: {
          lat: coordValidation.latitude,
          lng: coordValidation.longitude
        }
      };

      // Auto-save to camp_audit_data (non-blocking) to avoid repeated API calls
      // Use a separate try-catch so save failures don't block the user flow
      // IMPORTANT: Only save if place is actually a camp or youth program
      (async () => {
        try {
          console.log('💾 Checking if place should be saved to camp_audit_data...');
          // Fetch full place details for validation and saving (maximum field set for complete data)
          const fullPlaceDetails = await fetchPlaceDetails(suggestion.place_id, { fieldSet: 'maximum' });
          
          if (fullPlaceDetails && fullPlaceDetails.id) {
            // ✅ NEW: Validate using placeValidationService to prevent saving personal addresses
            // Pass the full placeData object (not just selected fields) for proper validation
            const validation = validateYouthProgram(fullPlaceDetails);
            
            if (!validation.isValid) {
              console.log('⏭️ Skipping save - place is not a valid youth program:', {
                name: fullPlaceDetails.name || fullPlaceDetails.displayName?.text || fullPlaceDetails.structured_formatting?.main_text,
                types: fullPlaceDetails.types,
                place_id: fullPlaceDetails.id,
                reason: validation.reason,
                confidence: validation.confidence
              });
              // IMPORTANT: Don't return here - validation failure should only prevent DB save, not search display
              // The place should still be displayed in search results even if not saved to database
              return;
            }
            
            console.log('✅ Place validated as camp/youth program, proceeding with save...', {
              name: fullPlaceDetails.name || fullPlaceDetails.displayName?.text || fullPlaceDetails.structured_formatting?.main_text,
              confidence: validation.confidence,
              reason: validation.reason
            });
            
            // Transform for database format
            const dbCampData = transformGooglePlaceForDB(fullPlaceDetails);
            
            if (dbCampData && dbCampData.place_id) {
              // Retry logic for failed saves (single retry)
              let saveResult = await saveCampToDatabase(dbCampData);
              
              if (!saveResult || !saveResult.success) {
                console.warn('⚠️ Initial save failed, retrying once...');
                // Wait 500ms before retry
                await new Promise(resolve => setTimeout(resolve, 500));
                saveResult = await saveCampToDatabase(dbCampData);
              }
              
              if (saveResult && saveResult.success) {
                console.log('✅ Auto-saved place from autocomplete to camp_audit_data:', saveResult.camp_id || dbCampData.place_id);
              } else {
                console.warn('⚠️ Save result did not indicate success after retry:', saveResult);
              }
            } else {
              console.warn('⚠️ Transformed data missing place_id, skipping save');
            }
          } else {
            console.warn('⚠️ Full place details missing or invalid, skipping save');
          }
        } catch (saveErr) {
          console.error('⚠️ Error auto-saving place from autocomplete (non-fatal):', saveErr);
          // Continue with selection even if save fails - don't block user flow
        }
      })();

      // Call the main callback with optional chaining
      onPlaceSelect?.(enrichedPlace);

      // Backward compatibility callbacks
      if (enrichedPlace.searchType === 'business' && onBusinessSelect) {
        onBusinessSelect({
          name: enrichedPlace.name,
          address: enrichedPlace.formatted_address,
          placeId: enrichedPlace.place_id,
          latitude: enrichedPlace.location.lat,
          longitude: enrichedPlace.location.lng,
          types: enrichedPlace.types,
          category: enrichedPlace.category,
          icon: enrichedPlace.icon,
          label: enrichedPlace.label,
          rating: enrichedPlace.rating,
          user_ratings_total: enrichedPlace.user_ratings_total,
          website: enrichedPlace.website,
          phone: enrichedPlace.formatted_phone_number,
          business_status: enrichedPlace.business_status
        });
      }

      onLocationSelect?.({
        address: enrichedPlace.formatted_address,
        placeId: enrichedPlace.place_id,
        latitude: enrichedPlace.location.lat,
        longitude: enrichedPlace.location.lng,
        types: enrichedPlace.types,
        name: enrichedPlace.name,
        category: enrichedPlace.category
      });

      // Start new session after selection
      newPlacesAPI.newSession();
      setError(null);

    } catch (err) {
      console.error('❌ LocationAutocomplete: Error getting place details:', {
        message: err.message,
        stack: err.stack,
        placeId: suggestion.place_id,
        suggestion: suggestion.description?.substring(0, 50)
      });
      
      // Provide user-friendly error messages
      let errorMessage = 'Unable to load full place details. Please try selecting again.';
      
      if (err.message && err.message.includes('API key')) {
        errorMessage = 'Google Places API is not configured. Please contact support.';
      } else if (err.message && err.message.includes('API key is invalid') || err.message.includes('API is not enabled')) {
        errorMessage = 'Google Places API configuration error. Please contact support.';
      } else if (err.message && (err.message.includes('coordinates') || err.message.includes('Invalid location'))) {
        errorMessage = 'Invalid location coordinates. Please try a different address.';
      } else if (err.message && err.message.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (err.message && (err.message.includes('network') || err.message.includes('fetch') || err.message.includes('Network error'))) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message && err.message.includes('Place not found')) {
        errorMessage = 'Location not found. Please try selecting a different address.';
      } else if (err.message) {
        // Use the error message from the API if available
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Even if API call fails, still call handlers with basic info from suggestion
      // The UI has already been updated above, so user sees their selection
      const basicData = {
        address: suggestion.description,
        placeId: suggestion.place_id,
        category: suggestion.category,
        searchType: suggestion.searchType
      };
      
      onPlaceSelect?.(basicData);
      
      if (suggestion.searchType === 'business') {
        onBusinessSelect?.(basicData);
      }
      onLocationSelect?.(basicData);
    } finally {
      setLoadingDetails(false);
    }
  }, [onPlaceSelect, onLocationSelect, onBusinessSelect, validateCoordinates]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        // If suggestions are showing and a suggestion is selected, select it
        if (showSuggestions && suggestions.length > 0 && selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          // Otherwise, trigger the onEnter callback if provided
          if (onEnter) {
            onEnter();
          }
        }
        break;
      case 'Escape':
        if (showSuggestions) {
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
        break;
    }
  }, [showSuggestions, suggestions, selectedIndex, handleSuggestionSelect, onEnter]);

  // Handle click outside to close suggestions (works with fixed positioning)
  useEffect(() => {
    if (!showSuggestions) return;
    
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target) &&
        !containerRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    // Use capture phase to catch clicks before they propagate
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showSuggestions]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Clear input
  const handleClear = useCallback(() => {
    setQuery('');
    
    // Call onChange prop to notify parent
    if (onChange) {
      onChange('');
    }
    
    setSuggestions([]);
    setShowSuggestions(false);
    setError(null);
    inputRef.current?.focus();
  }, [onChange]);

  // Calculate position for fixed dropdown (to escape overflow-hidden containers)
  const [dropdownStyle, setDropdownStyle] = useState({});
  
  // Update dropdown position when suggestions show or window resizes/scrolls
  const updateDropdownPosition = useCallback(() => {
    if (showSuggestions && suggestions.length > 0 && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      
      // For position: fixed, coordinates are relative to viewport (not document)
      // So we use getBoundingClientRect() directly without adding scroll offsets
      setDropdownStyle({
        position: 'fixed',
        top: `${inputRect.bottom + 8}px`, // 8px spacing below input
        left: `${inputRect.left}px`,
        width: `${inputRect.width}px`,
        maxWidth: '100vw', // Ensure it doesn't exceed viewport width
        zIndex: 99999 // Very high z-index to ensure it appears above all other elements including Card, Accordion, etc.
      });
    } else {
      setDropdownStyle({});
    }
  }, [showSuggestions, suggestions.length]);
  
  useEffect(() => {
    updateDropdownPosition();
    
    // Update position on scroll and resize
    if (showSuggestions && suggestions.length > 0) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSuggestions, suggestions.length, updateDropdownPosition]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900"
          placeholder={addressMode && placeholder === "Search camps, organizations, or locations" 
            ? "Search for an address, city, state, or zip code..." 
            : placeholder}
          disabled={disabled}
        />
        
        {/* Clear button */}
        {query && !loading && !loadingDetails && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Clear search"
          >
            <Icon name="x" size="sm" />
          </button>
        )}
        
        {/* Loading spinner - show for both search and details loading */}
        {(loading || loadingDetails) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Icon name="loader" size="sm" className="animate-spin text-brand-600" />
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="absolute top-full mt-1 text-sm text-red-600 bg-red-50 px-3 py-2 rounded border border-red-200 z-[9999]">
          {error}
        </div>
      )}

      {/* Suggestions dropdown - use Portal to escape overflow-hidden containers (like AccordionContent) */}
      {/* Portal renders dropdown to document.body to escape any overflow-hidden parent containers */}
      {typeof document !== 'undefined' && createPortal(
        showSuggestions && suggestions.length > 0 ? (
          <div 
            ref={suggestionsRef}
            style={dropdownStyle}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto pointer-events-auto"
            data-dropdown="location-autocomplete"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.place_id}
                className={`w-full text-left px-4 py-3 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors ${
                  selectedIndex === index 
                    ? "bg-purple-100 border-purple-200" 
                    : "hover:bg-gray-50 focus:bg-gray-50"
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-start gap-3">
                  {/* Icon based on type */}
                  <span className={`text-lg mt-0.5 flex-shrink-0 ${
                    selectedIndex === index ? "opacity-100" : "opacity-70"
                  }`}>
                    {suggestion.icon || '📍'}
                  </span>
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.structured_formatting?.main_text || suggestion.description}
                    </div>
                    
                    {/* Secondary text */}
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-xs text-gray-500 truncate mt-1">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : null,
        document.body
      )}
    </div>
  );
};

export default LocationAutocomplete;